import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/fleet/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Stars } from "@/components/Stars";
import { inputCls, primaryBtn } from "@/components/AuthLayout";
import { api, errMsg } from "@/lib/api";

const DFIELDS = [
  ["name", "Full name", "text"],
  ["phone", "Mobile", "tel"],
  ["email", "Email (login)", "email"],
  ["password", "Temporary password", "text"],
  ["license_no", "Licence number", "text"],
];

const AddDriver = ({ vehicles, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fleet/drivers", form);
      toast.success("Driver added and approved");
      setOpen(false);
      setForm({});
      onAdded();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-driver-button" className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-700/25 hover:bg-blue-600"><Plus className="h-4 w-4" /> Add driver</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add a Comfort Taxi driver</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="add-driver-form">
          {DFIELDS.map(([k, l, t]) => (
            <label key={k} className="block text-xs font-semibold text-slate-500">{l}
              <input data-testid={`new-driver-${k}-input`} required type={t} minLength={k === "password" ? 6 : undefined} className={`${inputCls} mt-1`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </label>
          ))}
          <label className="block text-xs font-semibold text-slate-500">Assign taxi
            <Select value={form.vehicle_id || ""} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
              <SelectTrigger data-testid="new-driver-vehicle-select" className="mt-1 h-12 rounded-xl"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>#{v.taxi_no} · {v.plate}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <button data-testid="add-driver-submit" className={`${primaryBtn} sm:col-span-2 mt-2`}>Save driver</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Actions = ({ d, act }) => (
  <div className="flex gap-2">
    {d.driver_status !== "approved" && <button data-testid={`approve-driver-${d.id}`} onClick={() => act(d.id, "approve")} className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500">Approve</button>}
    {d.driver_status === "pending" && <button data-testid={`reject-driver-${d.id}`} onClick={() => act(d.id, "reject")} className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50">Reject</button>}
    {d.driver_status === "approved" && <button data-testid={`suspend-driver-${d.id}`} onClick={() => act(d.id, "suspend")} className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">Suspend</button>}
  </div>
);

export const DriversPanel = ({ statusFilter, onChanged }) => {
  const [rows, setRows] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const load = () => {
    api.get("/fleet/drivers", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);
  const act = async (id, action) => {
    try {
      await api.post(`/fleet/drivers/${id}/${action}`);
      toast.success(`Driver ${action}d`);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const assign = async (id, vehicle_id) => {
    await api.put(`/fleet/drivers/${id}/vehicle`, { vehicle_id: vehicle_id === "none" ? null : vehicle_id });
    toast.success("Taxi assignment updated");
    load();
  };
  return (
    <div className="space-y-4">
      {!statusFilter && <div className="flex justify-end"><AddDriver vehicles={vehicles} onAdded={load} /></div>}
      <DataTable
        testId={statusFilter ? "pending-drivers-table" : "drivers-table"}
        rows={rows}
        empty={statusFilter ? "No drivers waiting for approval." : "No drivers yet."}
        cols={[
          ["Driver", (d) => <div><p className="font-semibold text-slate-900">{d.name}</p><p className="text-xs text-slate-400">{d.email}</p></div>],
          ["Phone", (d) => d.phone],
          ["Licence", (d) => d.license_no],
          ["Taxi ref", (d) => d.taxi_ref || "—"],
          ["Assigned taxi", (d) => (
            <Select value={d.vehicle_id || "none"} onValueChange={(v) => assign(d.id, v)}>
              <SelectTrigger data-testid={`assign-vehicle-select-${d.id}`} className="h-8 w-40 rounded-lg text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>#{v.taxi_no} · {v.plate}</SelectItem>)}
              </SelectContent>
            </Select>
          )],
          ["Trips", (d) => d.trips],
          ["Rating", (d) => (d.rating ? <span className="flex items-center gap-1.5" data-testid={`driver-rating-${d.id}`}><Stars value={d.rating.avg} size="h-3 w-3" /><span className="text-xs text-slate-500">{d.rating.avg.toFixed(1)} ({d.rating.count})</span></span> : <span className="text-xs text-slate-400">No ratings</span>)],
          ["Online", (d) => (d.is_online ? <span className="text-xs font-semibold text-emerald-600">● Online</span> : <span className="text-xs text-slate-400">Offline</span>)],
          ["Status", (d) => <StatusBadge status={d.driver_status} testId={`driver-status-${d.id}`} />],
          ["", (d) => <Actions d={d} act={act} />],
        ]}
      />
    </div>
  );
};
